import { useState } from 'react';
import { Alert, Box, Snackbar } from '@mui/material';
import { FormTitleBox } from '../../common/components';
import { usePrintConsumptionDelivery } from '../../hooks/useConsumptions';
import { getConsumptionApiErrorMessage } from './utils/consumptionsUtils';
import ConsumptionsDataTable from './components/ConsumptionsDataTable';
import ConsumptionsFilters from './components/ConsumptionsFilters';
import ConsumptionsPagination from './components/ConsumptionsPagination';
import ConsumptionDetailDialog from './components/ConsumptionDetailDialog';
import { useConsumptionsTable } from './hooks/useConsumptionsTable';

const ConsumptionsTable = () => {
  const [detailActionPending, setDetailActionPending] = useState(false);
  const [printFeedback, setPrintFeedback] = useState({ open: false, message: '', severity: 'info' });
  const printMutation = usePrintConsumptionDelivery();
  const {
    data,
    isLoading,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    filters,
    warehouses,
    detailDialogOpen,
    selectedConsumptionId,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleViewDetail,
    handleCloseDetailDialog,
    handleRefreshList,
  } = useConsumptionsTable();

  const handlePrintConsumption = async (consumption) => {
    if (!consumption?.uuid || printMutation.isPending) return;

    try {
      await printMutation.mutateAsync(consumption.uuid);
      setPrintFeedback({
        open: true,
        severity: 'success',
        message: 'Comprobante PDF descargado correctamente.',
      });
    } catch (error) {
      setPrintFeedback({
        open: true,
        severity: 'error',
        message: getConsumptionApiErrorMessage(error, 'No se pudo generar el comprobante PDF'),
      });
    }
  };

  return (
    <Box>
      <FormTitleBox
        title="Stock y compras | Consumos de stock"
        showSearch
        searchLabel="Buscar consumo"
        searchValue={searchTerm}
        onSearchChange={handleSearch}
        searchSize="medium"
        isLoading={detailActionPending || printMutation.isPending}
      />

      <ConsumptionsFilters
        filters={filters}
        warehouses={warehouses}
        onFilterChange={handleFilterChange}
        onSelectChange={handleFilterChange}
      />

      <ConsumptionsDataTable
        data={data}
        isLoading={isLoading}
        actionsDisabled={detailActionPending || printMutation.isPending}
        onView={handleViewDetail}
        onPrint={handlePrintConsumption}
      />

      {totalPages > 1 && (
        <ConsumptionsPagination
          totalPages={totalPages}
          totalCount={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      )}

      <ConsumptionDetailDialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        consumptionId={selectedConsumptionId}
        onRefreshList={handleRefreshList}
        onPendingChange={setDetailActionPending}
      />

      <Snackbar
        open={printFeedback.open}
        autoHideDuration={6000}
        onClose={() => setPrintFeedback((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={printFeedback.severity}
          onClose={() => setPrintFeedback((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {printFeedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConsumptionsTable;
